import { Component, Prop, h } from '@stencil/core';

const COMPONENT_NAME = 'dh-heading';

type AlignValue = 'left' | 'center' | 'right';
type VariantValue = '1' | '2' | '3' | '4' | '5' | '6';
type TagValue = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div';

/**
 * A semantic heading component with independent visual variant and HTML tag.
 * Supports responsive text alignment across breakpoints and ARIA accessibility
 * when rendered as a neutral `div`.
 *
 * @category Elements
 *
 * @slot - {required}, {content}, string
 *
 * @example
 *   <dh-heading variant="2" tag="h2" align="center">Accelerate your workflow</dh-heading>
 */
@Component({
  tag: 'dh-heading',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhHeading {
  /**
   * Visual variant of the heading (1–6).
   * Determines font size, line height, weight, and letter-spacing
   * according to the pdfFiller typographic scale.
   */
  @Prop() variant!: VariantValue;

  /**
   * The HTML tag used to render the heading.
   * Determines semantic importance (h1–h6) or neutral (`div`).
   * When `div` is used with a variant, `role="heading"` and `aria-level`
   * are applied automatically for accessibility.
   * @default "div"
   */
  @Prop() tag: TagValue = 'div';

  /**
   * Text alignment on mobile (base breakpoint).
   * @default "left"
   */
  @Prop() align: AlignValue = 'left';

  /**
   * Text alignment on small screens (>=768px).
   */
  @Prop() alignSm?: AlignValue;

  /**
   * Text alignment on medium screens (>=1024px).
   */
  @Prop() alignMd?: AlignValue;

  /**
   * Text alignment on large screens (>=1280px).
   */
  @Prop() alignLg?: AlignValue;

  render() {
    const Tag = this.tag;

    const classes = [
      COMPONENT_NAME,
      `${COMPONENT_NAME}--${this.variant}`,
      this.align !== 'left' ? `${COMPONENT_NAME}--align-${this.align}` : '',
      this.alignSm ? `${COMPONENT_NAME}--align-sm-${this.alignSm}` : '',
      this.alignMd ? `${COMPONENT_NAME}--align-md-${this.alignMd}` : '',
      this.alignLg ? `${COMPONENT_NAME}--align-lg-${this.alignLg}` : '',
    ].filter(Boolean).join(' ');

    const attrs: Record<string, string | undefined> = {};

    if (this.tag === 'div') {
      attrs['role'] = 'heading';
      attrs['aria-level'] = this.variant;
    }

    return (
      <Tag class={classes} {...attrs}>
        <slot />
      </Tag>
    );
  }
}
